(() => {
  const SELECTOR = '[data-sw-hero-slider]';
  // Theme Editor section reloads can execute this asset again.
  if (window.SlickwrapsHomepageHero) {
    window.SlickwrapsHomepageHero.initAll();
    return;
  }
  const cleanups = new WeakMap();

  function initHeroSlider(root) {
    if (!root || root.dataset.swHeroReady === 'true') return;

    const slides = Array.from(root.querySelectorAll('[data-sw-hero-slide]'));
    if (slides.length < 2) return;

    const dots = Array.from(root.querySelectorAll('[data-sw-hero-dot]'));
    const previous = root.querySelector('[data-sw-hero-previous]');
    const next = root.querySelector('[data-sw-hero-next]');
    const playback = root.querySelector('[data-sw-hero-playback]');
    const progress = root.querySelector('[data-sw-hero-progress]');
    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    );
    const mobileViewport = window.matchMedia('(max-width: 749px)');
    const autoplay = root.dataset.autoplay === 'true';
    const autoplaySpeed = Math.max(
      4000,
      Number(root.dataset.autoplaySpeed) || 7000
    );
    let activeIndex = 0;
    let timer = null;
    let remaining = autoplaySpeed;
    let startedAt = null;
    let progressAnimation = null;
    let destroyed = false;
    const listeners = [];
    const now = () => window.performance.now();
    let touchStartX = 0;
    // Keep the first mobile hero stable. Visitors can still swipe, use the
    // arrows, or opt into autoplay with the visible playback control.
    let isPaused = prefersReducedMotion.matches || mobileViewport.matches;
    let isPointerOver = false;
    let isFocusWithin = false;
    let pointerFocusPending = false;
    let isTouching = false;
    let pageLoaded = document.readyState === 'complete';

    function listen(target, type, callback, options) {
      if (!target?.addEventListener) return;
      target.addEventListener(type, callback, options);
      listeners.push(() => target.removeEventListener(type, callback, options));
    }

    function syncProgress(running) {
      if (!progress) return;
      const elapsed = autoplaySpeed - remaining;
      // No frame loop: the browser interpolates the SVG ring while the same
      // remaining-time counter controls the single slide-advance timeout.
      if (!progressAnimation && running && typeof progress.animate === 'function') {
        try {
          progressAnimation = progress.animate(
            [{ strokeDashoffset: '100' }, { strokeDashoffset: '0' }],
            { duration: autoplaySpeed, easing: 'linear', fill: 'both' }
          );
        } catch (_) {
          // The slider still works if SVG animations are unavailable.
        }
      }
      if (progressAnimation) {
        progressAnimation.pause();
        progressAnimation.currentTime = elapsed;
        if (running) progressAnimation.play();
      } else {
        progress.style.strokeDashoffset = String(100 * remaining / autoplaySpeed);
      }
    }

    function stopAutoplay() {
      if (startedAt !== null) {
        remaining = Math.max(0, remaining - (now() - startedAt));
      }
      startedAt = null;
      if (timer !== null) window.clearTimeout(timer);
      timer = null;
      syncProgress(false);
    }

    function scheduleAutoplay() {
      if (
        destroyed ||
        !autoplay ||
        !pageLoaded ||
        isPaused ||
        isPointerOver ||
        isFocusWithin ||
        isTouching ||
        document.hidden
      ) {
        stopAutoplay();
        return;
      }
      if (timer !== null) return;

      startedAt = now();
      syncProgress(true);
      timer = window.setTimeout(() => {
        timer = null;
        startedAt = null;
        remaining = 0;
        showSlide(activeIndex + 1);
      }, remaining);
    }

    function resetAutoplay() {
      stopAutoplay();
      remaining = autoplaySpeed;
      syncProgress(false);
    }

    function updatePlaybackControl() {
      if (!playback) return;

      const isPlaying = autoplay && !isPaused;
      const label = isPlaying ? 'Pause slideshow' : 'Play slideshow';
      playback.classList.toggle('is-paused', !isPlaying);
      playback.setAttribute('aria-label', label);
      playback.setAttribute('title', label);
    }

    function setPaused(paused) {
      isPaused = paused;
      updatePlaybackControl();

      if (isPaused) {
        stopAutoplay();
      } else {
        scheduleAutoplay();
      }
    }

    function hydrateSlideMedia(slide) {
      const responsiveSources = slide.querySelectorAll?.(
        '[data-sw-hero-srcset]'
      ) || [];
      const deferredImages = slide.querySelectorAll?.(
        '[data-sw-hero-src]'
      ) || [];

      responsiveSources.forEach((element) => {
        element.setAttribute(
          'srcset',
          element.dataset.swHeroSrcset
        );
        element.removeAttribute('data-sw-hero-srcset');
      });

      deferredImages.forEach((image) => {
        image.setAttribute('src', image.dataset.swHeroSrc);
        image.removeAttribute('data-sw-hero-src');
      });
    }

    function showSlide(index, userInitiated = false, animate = true) {
      if (destroyed) return;
      resetAutoplay();
      const nextIndex = (index + slides.length) % slides.length;
      const shouldAnimate =
        animate && nextIndex !== activeIndex && !mobileViewport.matches;
      activeIndex = nextIndex;

      hydrateSlideMedia(slides[activeIndex]);

      slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === activeIndex;
        slide.classList.toggle(
          'is-entering',
          isActive && shouldAnimate
        );
        slide.hidden = !isActive;
        slide.classList.toggle('is-active', isActive);
        slide.toggleAttribute('inert', !isActive);
        slide.setAttribute('aria-hidden', String(!isActive));
      });

      dots.forEach((dot, dotIndex) => {
        const isActive = dotIndex === activeIndex;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-current', String(isActive));
      });

      const background = slides[activeIndex].dataset.swHeroBackground;
      if (background) root.style.backgroundColor = background;

      if (userInitiated) isPaused = true;
      updatePlaybackControl();
      scheduleAutoplay();
    }

    listen(previous, 'click', () => {
      showSlide(activeIndex - 1, true);
    });

    listen(next, 'click', () => {
      showSlide(activeIndex + 1, true);
    });

    dots.forEach((dot) => {
      listen(dot, 'click', () => {
        showSlide(Number(dot.dataset.swHeroDot), true);
      });
    });

    listen(playback, 'click', () => {
      if (isPaused) {
        // An explicit Play request also works while hovering the control or
        // after a touch browser synthesizes mouseenter/focus events.
        isFocusWithin = false;
        isPointerOver = false;
      }
      setPaused(!isPaused);
    });

    listen(root, 'mouseenter', () => {
      isPointerOver = true;
      stopAutoplay();
    });
    listen(root, 'mouseleave', () => {
      isPointerOver = false;
      scheduleAutoplay();
    });
    listen(root, 'pointerdown', () => {
      pointerFocusPending = true;
    }, { capture: true });
    listen(root, 'pointerup', () => {
      pointerFocusPending = false;
    }, { capture: true });
    listen(root, 'pointercancel', () => {
      pointerFocusPending = false;
    }, { capture: true });
    listen(root, 'focusin', (event) => {
      if (root.contains(event.relatedTarget)) return;
      isFocusWithin = true;

      if (pointerFocusPending) {
        stopAutoplay();
      } else {
        // Keyboard navigation must not restart motion when focus leaves.
        setPaused(true);
      }
    });
    listen(root, 'focusout', (event) => {
      if (root.contains(event.relatedTarget)) return;
      isFocusWithin = false;
      scheduleAutoplay();
    });

    listen(root,
      'touchstart',
      (event) => {
        touchStartX = event.changedTouches[0]?.clientX || 0;
        isTouching = true;
        stopAutoplay();
      },
      { passive: true }
    );

    listen(root,
      'touchend',
      (event) => {
        const touchEndX = event.changedTouches[0]?.clientX || 0;
        const distance = touchEndX - touchStartX;
        isTouching = false;

        if (Math.abs(distance) > 50) {
          showSlide(activeIndex + (distance < 0 ? 1 : -1), true);
        } else {
          scheduleAutoplay();
        }
      },
      { passive: true }
    );

    listen(root, 'touchcancel', () => {
      isTouching = false;
      scheduleAutoplay();
    }, { passive: true });

    listen(document, 'visibilitychange', scheduleAutoplay);

    listen(prefersReducedMotion, 'change', (event) => {
      if (event.matches) setPaused(true);
    });

    if (!pageLoaded) {
      listen(window,
        'load',
        () => {
          pageLoaded = true;
          scheduleAutoplay();
        },
        { once: true }
      );
    }

    root.dataset.swHeroReady = 'true';
    root.classList.add('is-slider-ready');
    cleanups.set(root, () => {
      destroyed = true;
      stopAutoplay();
      progressAnimation?.cancel();
      if (progress) progress.style.strokeDashoffset = '100';
      listeners.forEach((remove) => remove());
      cleanups.delete(root);
      delete root.dataset.swHeroReady;
      root.classList.remove('is-slider-ready');
    });
    showSlide(0, false, false);
  }

  function initAll(scope = document) {
    if (scope.matches?.(SELECTOR)) initHeroSlider(scope);
    scope.querySelectorAll?.(SELECTOR).forEach(initHeroSlider);
  }

  window.SlickwrapsHomepageHero = { initAll };
  initAll();

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const scope = event.target;
    if (scope.matches?.(SELECTOR)) cleanups.get(scope)?.();
    scope.querySelectorAll?.(SELECTOR).forEach((root) => cleanups.get(root)?.());
  });
})();
